(ns web3.hints.core
  (:require [web3.view :as v]
            [re-frame-simple.db :as db]
            ["react-dom" :as dom]
            ["react" :as react]
            [reagent.core :as r]
            [web3.ui-state :as ui-state]
            [goog.events :as events]
            [web3.utils :as utils]
            [web3.persistence.local :as local]
            [web3.hints.content :as content]
            [re-frame.core :as rf]
            [web3.view.hiccup :as hiccup]
            [web3.icons.shapes :as shapes]
            [web3.utils :as utils]
            [applied-science.js-interop :as j]))

(def triangle-wh [16 9])
(def max-width 225)
(def min-height 80)
(def hint-element-id "hints")

(rf/reg-event-db :update-hints
                 (fn [db [_ key f & args]]
                   (apply update db key f args)))


(defn activate-hint-mode! []
  (local/init-storage [:hints :dismissed])
  (db/assoc-in! [:hints :hint-mode?] true))

(comment
 ;; for dev
 (activate-hint-mode!))

(defn window-height []
  (j/get-in js/document [:documentElement :clientHeight]))

(defn window-width []
  (j/get-in js/document [:documentElement :clientWidth]))

(defn hint-mode? []
  (db/get-in [:hints :hint-mode?]))

(defn show! [hints]
  (js/setTimeout #(when (hint-mode?)
                    (let [dismissed (db/get-in [:hints :dismissed])]
                      (db/update-in! [:hints :active] merge (first (for [hint-key (cond->> hints
                                                                                           dismissed (remove dismissed))
                                                                         :let [hint-data (get content/hint-content hint-key)]
                                                                         :when (utils/get-element (:selector hint-data))]
                                                                     {hint-key hint-data})))))
                 150))

(defn force-show!
  [hints]
  (doall
   (for [hint-key hints
         :let [hint-data (get content/hint-content hint-key)]
         :when (utils/get-element (:selector hint-data))]
     (db/update-in! [:hints :active] merge {hint-key hint-data}))))

(defn dismissed? [hint-key]
  (contains? (db/get-in [:hints :dismissed]) hint-key))

(defn dismiss! [hint-keys]
  (rf/dispatch (into [:update-hints]
                     [:hints (fn [{:as hints
                                   active-hints :active}]
                               (-> hints
                                   (assoc :active {})
                                   (update :dismissed (fnil into #{}) (keys active-hints))))])))

(defn center-points
  "Given dom element `el`, returns :top and :left for center positions on each side of the element,
   as well as :space-remaining, the distance from that point to the edge of the window in its direction."
  [el]
  (let [box (.getBoundingClientRect el)
        top (.-top box)
        left (.-left box)
        height (.-height box)
        width (.-width box)]
    {:left {:left left
            :top (+ top (/ height 2))
            :space-remaining (.-left box)}
     :top {:left (+ left
                    (/ width 2))
           :top top
           :space-remaining (.-top box)}
     :right {:left (+ left width)
             :top (+ top
                     (/ height 2))
             :space-remaining (- (window-width)
                                 (.-right box))}
     :bottom {:left (+ left
                       (/ width 2))
              :top (+ top height)
              :space-remaining (- (window-height)
                                  (.-top box))}}))

(defn triangle [side]
  (let [transform (str "rotate(" (case side :top 180
                                            :right 270
                                            :bottom 0
                                            :left 90) "deg)")]
    (-> shapes/triangle
        (hiccup/class "fade-in")
        (hiccup/styles {:transform transform
                        :margin-left (- (/ (first triangle-wh) 2))})
        (update 1 assoc :key (str side "-triangle"))
        (hiccup/size (first triangle-wh) (second triangle-wh)))))

(defn direction [{:keys [top left]}]
  [(if (> top (/ (window-height) 2))
     :upward
     :downward)
   (if (> left (/ (window-width) 2))
     :leftward
     :rightward)])

(defn content-position [side {:keys [top left] :as coords}]
  (let [w-width (window-width)
        w-height (window-height)
        [triangle-w triangle-h] triangle-wh
        pointer-pad (/ triangle-h 2)
        [v-direction h-direction] (direction coords)]
    (merge
     {:max-width max-width}

     ;; vertical position
     (case side
       (:left :right) (case v-direction
                        :upward {:bottom (-> w-height
                                             (- top
                                                (/ triangle-w 2)
                                                pointer-pad))}
                        :downward {:top (-> top
                                            (- (/ triangle-w 2)
                                               pointer-pad))})
       :top {:bottom (-> w-height
                         (- top)
                         (+ triangle-h))}
       :bottom {:top (-> top
                         (+ triangle-h))})

     ;; horizontal position
     (case side
       :left {:right (-> w-width
                         (- left)
                         (+ triangle-h))}
       :right {:left (-> left
                         (+ triangle-h))}
       (:top :bottom) (merge {:width max-width
                              :left (max 8 (- left (/ max-width 2)))})))))

(defn enough-space? [side {:keys [space-remaining top]}]
  (case side
    (:left :right) (and (>= space-remaining max-width)
                        (> top (/ min-height 2)))
    (:top :bottom) (>= space-remaining min-height)))

(defn hint-position [side target]
  (let [points (center-points target)]
    (or (first (for [side [side :right :bottom :left :top]
                     :when (enough-space? side (get points side))]
                 [side (get points side)]))
        [side (get points side)])))

(defn closest-z-index [element]
  (loop [element element
         z-index 0]
    (if (not element)
      z-index
      (recur (.-parentElement element)
             (let [next-z (some-> (js/window.getComputedStyle element)
                                  (.getPropertyValue "z-index")
                                  (js/parseInt))]
               (if (js/isNaN next-z)
                 z-index
                 (max z-index next-z)))))))

(v/defview hint
  {:component-did-mount (fn [^js this]
                          (js/setTimeout #(.forceUpdate this)
                                         200))}
  [{:as hint
    :keys [selector
           content
           side]
    :or {z-index 0}}]
  @ui-state/ui-globals
  (when-let [target (utils/get-element selector)]
    (let [[side coords] (hint-position side target)
          coords (dissoc coords :space-remaining)
          z-index (inc (closest-z-index target))]

      [:div
       [:div.fixed.pad-h-3.pad-v-2_5.text-black.round-2.text-size-16.bg-light-green.line-h-20.pop-in
        {:style (-> (content-position side coords)
                    (assoc :z-index z-index
                           :box-shadow "0 8px 20px rgba(0,0,0,0.2)"))}
        (utils/eval-if-fn content)]
       (-> (triangle side)
           (hiccup/class "fixed text-light-green")
           (hiccup/styles (-> coords
                              (assoc :z-index z-index))))])))

(v/defview show-hints*
  {:component-did-mount
   (fn []
     (events/listen js/window #js ["click" "touchstart" "keydown"] dismiss!))}
  []
  (into [:div]
        (for [[id hint-data] (db/get-in [:hints :active])]
          [hint (assoc hint-data :id id)])))

(def show-hints (r/reactify-component show-hints*))

(v/defview render-hints []
  (dom/createPortal
   (react/createElement show-hints)
   (v/find-or-insert-id hint-element-id)))